/**
 * @fileoverview Hook for Phase 2 cache data bootstrap.
 *
 * This hook manages the non-blocking background fetch of cache data after
 * the critical bootstrap phase completes. Cache data includes items, spells,
 * treasures, moons, and weather conditions - reference data that is useful
 * but not required for initial UI render.
 *
 * ## Key Characteristics
 *
 * 1. **Non-Blocking**: Cache fetch runs in the background after critical data loads.
 *    The UI can render and function while this hook is still loading.
 *
 * 2. **Non-Fatal Errors**: Cache failures are logged as warnings but do not
 *    prevent the application from functioning. The app gracefully degrades
 *    without cache data.
 *
 * 3. **Idempotent**: Multiple calls or re-renders do not trigger duplicate
 *    fetches. The hook uses a ref to track completion state.
 *
 * 4. **Cancellable**: Respects the provided AbortSignal for cleanup on
 *    component unmount or explicit cancellation.
 *
 * ## Usage
 *
 * This hook is typically used by the bootstrap orchestration logic after
 * critical data has been successfully loaded.
 *
 * ```tsx
 * function BootstrapOrchestrator() {
 *   const abortControllerRef = useRef(new AbortController());
 *   const [criticalComplete, setCriticalComplete] = useState(false);
 *
 *   // Critical bootstrap logic...
 *   // On success: setCriticalComplete(true);
 *
 *   const { isLoading: isCacheLoading, error: cacheError } = useCacheBootstrap({
 *     criticalComplete,
 *     signal: abortControllerRef.current.signal,
 *   });
 *
 *   // Cache errors are non-fatal; we can still render the UI
 *   if (cacheError) {
 *     console.warn('Cache load failed, some features may be limited:', cacheError);
 *   }
 *
 *   return isCacheLoading ? <CacheLoadingIndicator /> : <MainUI />;
 * }
 * ```
 *
 * @see react-ui/src/hooks/bootstrap/types.ts for CacheBootstrapResponse type
 * @see react-ui/src/hooks/bootstrap/useFetchWithRetry.ts for fetch logic
 * @see docs/adr/0002-bootstrap-state-hydration.md for architecture decisions
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useGlobalState } from '../../state/useGlobalState';
import { fetchWithRetry } from './useFetchWithRetry';
import { type CacheBootstrapResponse, isCacheBootstrapResponse } from './types';
import { logger } from '../../services/logger.client';

// =============================================================================
// Types
// =============================================================================

/**
 * Parameters for the useCacheBootstrap hook.
 */
export interface UseCacheBootstrapParams {
  /**
   * Flag indicating whether critical bootstrap has completed.
   * The cache fetch will only start when this becomes true.
   */
  criticalComplete: boolean;

  /**
   * AbortSignal for cancellation support.
   * Pass a signal from an AbortController to enable cancellation on
   * component unmount or explicit abort.
   */
  signal: AbortSignal;
}

/**
 * Result returned by the useCacheBootstrap hook.
 *
 * Note: Cache errors are non-fatal. The `error` property is provided
 * for reporting/logging purposes only - it should not be treated as
 * a blocking error.
 */
export interface UseCacheBootstrapResult {
  /**
   * True while the cache fetch is in progress.
   * False when the fetch completes (success or failure) or hasn't started.
   */
  isLoading: boolean;

  /**
   * The error if the cache fetch failed.
   * This is for reporting only - cache errors are non-fatal and
   * should not block the application.
   * Will be null if the fetch succeeded, hasn't started, or was aborted.
   */
  error: Error | null;
}

// =============================================================================
// Constants
// =============================================================================

/**
 * API endpoint for cache bootstrap data.
 */
const CACHE_BOOTSTRAP_ENDPOINT = '/api/bootstrap/cache';

/**
 * Label for logging and error messages.
 */
const FETCH_LABEL = 'cache bootstrap';

// =============================================================================
// Hook Implementation
// =============================================================================

/**
 * Hook for Phase 2 cache data bootstrap.
 *
 * Fetches non-critical cache data (items, spells, treasures, moons, weather
 * conditions) after critical bootstrap completes. This data is useful but
 * not required for initial UI render.
 *
 * ## Behavior
 *
 * - **Waits for criticalComplete**: No fetch is initiated until `criticalComplete` is true.
 * - **Idempotent**: Uses a ref to track completion and prevent duplicate fetches.
 * - **Non-fatal errors**: Errors are logged as warnings and returned for reporting,
 *   but do not prevent the application from functioning.
 * - **Cancellable**: Respects the provided AbortSignal for clean cancellation.
 *
 * ## Error Handling
 *
 * Cache failures are intentionally non-fatal:
 * - AbortError: Handled silently (clean unmount or explicit cancellation)
 * - Other errors: Logged as warnings, returned in `error` property
 *
 * The application can function without cache data, with features becoming
 * available if/when the data loads successfully.
 *
 * @param params - Hook parameters including criticalComplete flag and AbortSignal
 * @returns Object with `isLoading` and `error` properties
 *
 * @example
 * ```tsx
 * const { isLoading, error } = useCacheBootstrap({
 *   criticalComplete: true,
 *   signal: abortController.signal,
 * });
 *
 * // Show optional cache loading indicator
 * if (isLoading) {
 *   return <span className="cache-loading">Loading additional data...</span>;
 * }
 *
 * // Optionally warn about cache failures (non-blocking)
 * if (error) {
 *   console.warn('Cache unavailable:', error.message);
 * }
 * ```
 */
export function useCacheBootstrap({
  criticalComplete,
  signal,
}: UseCacheBootstrapParams): UseCacheBootstrapResult {
  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // ---------------------------------------------------------------------------
  // Refs for idempotency
  // ---------------------------------------------------------------------------

  /**
   * Tracks whether the cache fetch has completed successfully.
   * Prevents duplicate fetches on re-renders or React Strict Mode double-mount.
   */
  const hasCompletedRef = useRef(false);

  /**
   * Tracks whether a fetch is currently in progress.
   * Prevents concurrent fetches if the hook re-renders during a fetch.
   */
  const isFetchingRef = useRef(false);

  /**
   * Tracks whether the component is still mounted.
   * Prevents state updates after unmount.
   */
  const isMountedRef = useRef(true);

  // ---------------------------------------------------------------------------
  // Store access
  // ---------------------------------------------------------------------------

  /**
   * Get hydrateState directly from the store.
   * We access this outside of the effect to avoid it being a dependency.
   * The function reference is stable (defined in the store creator).
   */
  const hydrateState = useGlobalState.getState().hydrateState;

  // ---------------------------------------------------------------------------
  // Fetch logic
  // ---------------------------------------------------------------------------

  /**
   * Performs the cache bootstrap fetch.
   *
   * This function is defined with useCallback for stability, but the main
   * protection against duplicate fetches comes from the refs.
   */
  const performCacheFetch = useCallback(async (): Promise<void> => {
    // Guard: Already completed - skip
    if (hasCompletedRef.current) {
      logger.debug(
        'useCacheBootstrap: Cache already loaded, skipping fetch.'
      );
      return;
    }

    // Guard: Already fetching - skip
    if (isFetchingRef.current) {
      logger.debug(
        'useCacheBootstrap: Fetch already in progress, skipping duplicate.'
      );
      return;
    }

    // Guard: Signal already aborted - skip
    if (signal.aborted) {
      logger.debug(
        'useCacheBootstrap: Signal already aborted, skipping fetch.'
      );
      return;
    }

    // Mark as fetching
    isFetchingRef.current = true;

    // Update loading state if mounted
    if (isMountedRef.current) {
      setIsLoading(true);
      setError(null);
    }

    logger.info('useCacheBootstrap: Starting cache data fetch.');

    try {
      // Perform the fetch with retry logic
      const response = await fetchWithRetry<CacheBootstrapResponse>(
        CACHE_BOOTSTRAP_ENDPOINT,
        signal,
        FETCH_LABEL
      );

      // Validate response structure
      if (!isCacheBootstrapResponse(response)) {
        throw new Error(
          'Invalid cache bootstrap response: missing required fields (items, spells, treasures, moons, weatherConditions)'
        );
      }

      // Check if aborted while processing (after fetch completed)
      if (signal.aborted) {
        logger.info(
          'useCacheBootstrap: Signal aborted after fetch, discarding results.'
        );
        return;
      }

      logger.info(
        {
          itemCount: response.items?.length ?? 0,
          spellCount: response.spells?.length ?? 0,
          treasureCount: response.treasures?.length ?? 0,
          moonCount: response.moons?.length ?? 0,
          weatherConditionCount: response.weatherConditions?.length ?? 0,
        },
        'useCacheBootstrap: Cache data received successfully.'
      );

      // Hydrate the cache data into the global store
      // The hydrateState function handles merging into session.cache
      // We cast to Partial<CacheState> since the response may not include all cache fields
      // The hydrateState function explicitly checks each field before assigning
      hydrateState({ cache: response as unknown as Partial<import('../../state/cacheActions').CacheState> } as Parameters<typeof hydrateState>[0]);

      // Mark as completed to prevent future fetches
      hasCompletedRef.current = true;

      logger.info('useCacheBootstrap: Cache data hydrated successfully.');
    } catch (err: unknown) {
      // Handle AbortError silently - this is expected on unmount or explicit cancel
      if (err instanceof DOMException && err.name === 'AbortError') {
        logger.debug(
          'useCacheBootstrap: Fetch aborted (likely unmount or cancellation). Ignoring.'
        );
        // Don't set error state for abort - it's a clean cancellation
        return;
      }

      // For all other errors, log as warning (non-fatal) and set error state
      const errorToStore = err instanceof Error ? err : new Error(String(err));

      logger.warn(
        {
          errorName: errorToStore.name,
          errorMessage: errorToStore.message,
        },
        'useCacheBootstrap: Failed to fetch cache data. Some features may be limited.'
      );

      // Update error state for reporting (if still mounted)
      if (isMountedRef.current) {
        setError(errorToStore);
      }
    } finally {
      // Clear fetching flag
      isFetchingRef.current = false;

      // Update loading state if mounted
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [signal, hydrateState]);

  // ---------------------------------------------------------------------------
  // Effect: Trigger fetch when criticalComplete becomes true
  // ---------------------------------------------------------------------------

  useEffect(() => {
    // Mark as mounted
    isMountedRef.current = true;

    logger.debug(
      { criticalComplete, hasCompleted: hasCompletedRef.current },
      'useCacheBootstrap: Effect triggered.'
    );

    // Only start fetch when critical bootstrap is complete
    if (criticalComplete) {
      // Use void to explicitly ignore the promise (we handle errors internally)
      void performCacheFetch();
    }

    // Cleanup: mark as unmounted to prevent state updates
    return () => {
      isMountedRef.current = false;
      logger.debug('useCacheBootstrap: Cleanup - component unmounting.');
    };
  }, [criticalComplete, performCacheFetch]);

  // ---------------------------------------------------------------------------
  // Return result
  // ---------------------------------------------------------------------------

  return {
    isLoading,
    error,
  };
}

// =============================================================================
// Utility: Reset function for testing
// =============================================================================

/**
 * Resets the internal completion state of the cache bootstrap.
 *
 * This is primarily useful for testing scenarios where you need to reset
 * the hook's internal state between test runs. In production, the hook's
 * idempotency is preserved across the component lifecycle.
 *
 * **Note**: This function creates a new hook instance with reset state.
 * It does not affect existing hook instances.
 *
 * @internal This is exported for testing purposes only.
 */
export function createCacheBootstrapResetter(): () => void {
  // The refs are instance-specific to each hook call, so this factory
  // creates a closure that can be used to track and reset state if needed.
  // However, since refs are local to each hook instance, the primary use
  // is in test utilities that need to ensure a fresh state.
  let _shouldReset = false;

  return () => {
    _shouldReset = true;
    logger.debug('useCacheBootstrap: Reset requested for next hook instance.');
  };
}
